import { aiLanguageDirective } from './i18n';

const MODEL = 'claude-sonnet-4-6';
// Haiku is ~3x cheaper on output. Used ONLY for the low-stakes, templated
// follow-up questions — never for the actual advice, which stays on Sonnet.
const HAIKU_MODEL = 'claude-haiku-4-5';

// Extended thinking: the model reasons privately before answering. We turn this
// on ONLY for the two consequential judgment calls — Referral (does this child
// need outside help?) and MTSS (which support tier?) — where being right matters
// most. It is deliberately NOT used on Brain Science, whose instant streaming is
// what makes the app feel fast. The private thinking is never shown to teachers
// (our proxy only forwards the final answer text). Note: with thinking on,
// max_tokens must leave room for the thinking tokens AND the visible answer, so
// the two callers below use a higher ceiling than they would otherwise need.
const ADAPTIVE_THINKING = { type: 'adaptive' as const };

// All Anthropic calls go through our own Netlify Edge Function, which holds the
// API key server-side and streams the response back. No API key is ever present
// in this browser code.
const FUNCTION_URL = '/api/anthropic';

// Scope line appended to EVERY system prompt (via `ask`, `askStream`, and the
// direct callers below). Keep this identical everywhere — it states plainly that
// the tool does not diagnose, treat, advise clinically, or decide a student's
// care. This sits alongside, not in place of, the existing "Never diagnose" and
// "non-clinical language" guardrails.
const SCOPE = `\n\nThis tool explains general brain and behavior science and classroom practice to an educator. It does not diagnose, does not provide treatment, does not provide clinical advice, and does not determine what care a student receives. Those decisions belong to licensed professionals and the school's team.`;

// Cross-cutting instructions appended to EVERY system prompt: the scope guard +
// the active-language directive. Sent to the Edge Function separately as `guards`
// so that when the Edge swaps in a parent prompt variant (Parent Mode, selected
// server-side by role) it can re-append these to the parent prompt too.
const guardString = () => SCOPE + aiLanguageDirective();
const withGuards = (system: string) => system + guardString();

// Parent Mode. The account role is mirrored here so the user-message labels match
// the reader (a parent at home vs. an educator). The SYSTEM prompt variant itself
// is chosen SERVER-SIDE in the Edge Function from the session role — this is only
// the client-side framing of the data we send. Default teacher.
let ACCOUNT_ROLE: 'teacher' | 'parent' = 'teacher';
export function setAccountRole(role: string | null | undefined) {
  ACCOUNT_ROLE = role === 'parent' ? 'parent' : 'teacher';
}
export function getAccountRole(): 'teacher' | 'parent' { return ACCOUNT_ROLE; }

// Role-aware subject line for the user message. Teacher keeps the EXACT
// "Student: ${initials}, Grade: ${grade}" shape (guarded by the grade-label
// test and required to be byte-for-byte unchanged). Parent uses child-appropriate
// labels; the local aliases deliberately avoid re-triggering that guarded literal.
function subjectLine(initials: string, grade: string): string {
  if (ACCOUNT_ROLE === 'parent') {
    const name = initials, ageOrGrade = grade;
    return `Child: ${name}, age or grade: ${ageOrGrade}`;
  }
  return `Student: ${initials}, Grade: ${grade}`;
}
const describerLabel = () => (ACCOUNT_ROLE === 'parent' ? 'Parent description' : 'Teacher description');

// Core request. The Edge Function streams back the model's text as plain UTF-8.
// onChunk (optional) is called with the accumulated text every time more arrives
// — used for live "typing" display. Returns the complete text when finished.
// The signed-in teacher is identified server-side from the session cookie (sent
// automatically on this same-origin request) — the browser no longer asserts its
// own identity for rate-limiting.
async function streamText(payload: any, onChunk?: (full: string) => void, signal?: AbortSignal): Promise<string> {
  const r = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin', // send the session cookie so the server can identify the teacher
    body: JSON.stringify(payload),
    signal
  });
  if (!r.ok) {
    const errBody = await r.json().catch(() => ({}));
    const err: any = new Error(errBody?.error?.message || `HTTP ${r.status}`);
    err.status = r.status;
    err.code = errBody?.error?.code;
    throw err;
  }
  if (!r.body) return await r.text();
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let full = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    full += decoder.decode(value, { stream: true });
    if (onChunk) onChunk(full);
  }
  full += decoder.decode();
  if (onChunk) onChunk(full);
  return full;
}

// Drain the stream fully and return the complete text (no live display).
async function callAnthropic(payload: any, signal?: AbortSignal): Promise<string> {
  return streamText(payload, undefined, signal);
}

// maxTokens is a SAFETY CEILING, not a target — you're only billed for what the
// model actually writes. We set each caller a realistic cap so a rare runaway
// generation can't produce a surprise bill. `model` stays on Sonnet for every
// advice call; only the follow-up questions opt into the cheaper Haiku model.
async function ask(system: string, user: string, maxTokens = 2000, model: string = MODEL, promptKey?: string): Promise<string> {
  return callAnthropic({ model, max_tokens: maxTokens, system: withGuards(system), guards: guardString(), promptKey, role: ACCOUNT_ROLE, messages: [{ role: 'user', content: user }] });
}

// Stream a plain-prose answer, calling onChunk with the growing text as it arrives.
// `thinking` (optional) enables extended thinking; the thinking tokens count
// toward maxTokens, so pass a higher ceiling when using it.
async function askStream(system: string, user: string, onChunk: (full: string) => void, maxTokens = 1000, thinking?: any, promptKey?: string): Promise<string> {
  const payload: any = { model: MODEL, max_tokens: maxTokens, system: withGuards(system), guards: guardString(), promptKey, role: ACCOUNT_ROLE, messages: [{ role: 'user', content: user }] };
  if (thinking) payload.thinking = thinking;
  return streamText(payload, onChunk);
}

function safeParseJSON(text: string): any {
  try {
    const clean = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    return JSON.parse(clean);
  } catch {
    const match = text.match(/\{[\s\S]*\}/);
    if (match) {
      try { return JSON.parse(match[0]); } catch {}
    }
    return null;
  }
}

export async function getFollowUpQuestions(input: string): Promise<string[]> {
  const s = `You help K-12 teachers understand struggling students. Generate exactly 2 short empathetic follow-up questions to better understand the situation. Return ONLY a JSON array of 2 question strings. Example: ["How long has this been happening?", "Has anything changed at home recently?"]`;
  try {
    // Low-stakes, templated output → cheaper Haiku model. Tiny cap (2 questions).
    const result = await ask(s, input, 200, HAIKU_MODEL, 'followup');
    const parsed = safeParseJSON(result);
    if (Array.isArray(parsed) && parsed.length >= 2) return parsed.slice(0, 2);
    return ["How long has this been happening?", "How is this situation affecting you personally?"];
  } catch (e) {
    console.error('Decode My Student — getFollowUpQuestions failed, using fallback:', e);
    return ["How long has this been happening?", "How is this situation affecting you personally?"];
  }
}

// ---- Section-by-section generation ----
// Each function below generates ONE section only. Beyond the raw description,
// each also receives an optional `context` block — the teacher's answers to the
// follow-up questions plus a summary of past conversations about this student —
// so every section reasons from everything gathered, not just the opening words.
// Sections never see each other's output. Prose sections stream; short
// structured sections return parsed JSON.

const escalationCheck = (input: string) =>
  input.toLowerCase().includes('out of my class') || input.toLowerCase().includes('remove') ||
  input.toLowerCase().includes("can't do this") || input.toLowerCase().includes('give up');

// Appends the follow-up answers + prior-conversation context under a clear
// label, so the model treats it as extra detail (not part of the description).
const contextBlock = (context?: string) =>
  context && context.trim() ? `\n\nAdditional context the ${ACCOUNT_ROLE === 'parent' ? 'parent' : 'teacher'} provided (their answers to follow-up questions, and past conversations about this ${ACCOUNT_ROLE === 'parent' ? 'child' : 'student'}):\n${context.trim()}` : '';

// On submit: generate ONLY the summary + top 3 (short, structured, fast).
export async function getSummary(input: string, initials: string, grade: string, context?: string): Promise<any> {
  const hasContext = !!(context && context.trim());
  const s = `You are a warm brain health expert for K-12 teachers. Never diagnose. Use plain language. Be specific to exactly what the teacher described — reference their actual words.
${hasContext ? 'Additional context (follow-up answers and any past conversations) is provided below — factor it into your thinking.' : ''}
Return ONLY valid JSON — no markdown, no backticks:
{"summary":"3-4 warm plain-language sentences specific to what was described — reference what the teacher said","top3":["specific actionable strategy 1","specific actionable strategy 2","specific actionable strategy 3"]}`;
  const userMsg = `${subjectLine(initials, grade)}
${describerLabel()}: ${input}${contextBlock(context)}`;
  // meter:'workup' tells the server to count this as one workup — counted there,
  // on the server, only if the generation succeeds (never on an error/timeout).
  const result = await callAnthropic({ model: MODEL, max_tokens: 700, system: withGuards(s), guards: guardString(), promptKey: 'summary', role: ACCOUNT_ROLE, messages: [{ role: 'user', content: userMsg }], meter: 'workup' });
  const parsed = safeParseJSON(result);
  if (parsed && parsed.summary) return parsed;
  throw new Error('AI_ERROR: Invalid summary response');
}

// Brain Science: streamed prose.
export async function streamBrainScience(input: string, initials: string, grade: string, onChunk: (t: string) => void, context?: string): Promise<string> {
  const s = `You are a warm brain health expert for K-12 teachers. Never diagnose. Plain language only, with everyday analogies. Be specific to what was described.
Explain, in about 2 short paragraphs, the brain science behind what this student is doing — what their brain and nervous system are likely doing, and why. Warm, plain, no clinical jargon. Flowing prose — no bullet points, no headings.`;
  const userMsg = `${subjectLine(initials, grade)}
${describerLabel()}: ${input}${contextBlock(context)}`;
  return askStream(s, userMsg, onChunk, 900, undefined, 'brain');
}

// Strategies: short, structured (academic / behavioral / relational).
export async function getStrategies(input: string, initials: string, grade: string, context?: string): Promise<any> {
  const s = `You are a warm brain health expert for K-12 teachers. Never diagnose. Plain language only. Be specific to what was described.
Return ONLY valid JSON — no markdown, no backticks:
{"academic":["specific strategy 1","specific strategy 2","specific strategy 3"],"behavioral":["specific strategy 1","specific strategy 2","specific strategy 3"],"relational":["specific strategy 1","specific strategy 2","specific strategy 3"]}`;
  const userMsg = `${subjectLine(initials, grade)}
${describerLabel()}: ${input}${contextBlock(context)}`;
  const result = await callAnthropic({ model: MODEL, max_tokens: 900, system: withGuards(s), guards: guardString(), promptKey: 'strategies', role: ACCOUNT_ROLE, messages: [{ role: 'user', content: userMsg }] });
  const parsed = safeParseJSON(result);
  if (parsed && parsed.academic) return parsed;
  throw new Error('AI_ERROR: Invalid strategies response');
}

// For You (teacher support): streamed prose.
export async function streamForYou(input: string, initials: string, grade: string, onChunk: (t: string) => void, context?: string): Promise<string> {
  const isEscalated = escalationCheck(`${input} ${context || ''}`);
  const s = `You are a warm brain health expert for K-12 teachers. Speak directly to the teacher with genuine, specific empathy — name what THIS teacher expressed, not generic support.
${isEscalated ? 'IMPORTANT: The teacher has expressed wanting to remove this student — address this directly and compassionately.' : ''}
Write about 2 short paragraphs, warm and personal. Flowing prose — no bullet points, no headings.`;
  const userMsg = `${subjectLine(initials, grade)}
${describerLabel()}: ${input}${contextBlock(context)}`;
  return askStream(s, userMsg, onChunk, 700, undefined, 'forYou');
}

// Referral guidance: streamed prose.
export async function streamReferral(input: string, initials: string, grade: string, onChunk: (t: string) => void, context?: string): Promise<string> {
  const s = `You are a warm brain health educator for K-12 teachers. Never diagnose. Plain language only.
Help the teacher prepare to bring this situation to the people at school who own support decisions. Do two things: (1) list the specific, observable things worth documenting from what the teacher described — concrete behaviors, patterns, frequency, and context, not conclusions or labels; and (2) name who at the school owns the decision about next steps — typically the school counselor, the student support / MTSS team, or an administrator — and what the teacher should bring to that conversation. Report what was observed and who to loop in. Do NOT state whether the student needs outside help, and do NOT decide that a referral is or is not warranted — that determination belongs to the school's team. Write 3-4 warm, specific paragraphs. Flowing prose — no bullet points, no headings.`;
  const userMsg = `${subjectLine(initials, grade)}. Situation: ${input}${contextBlock(context)}`;
  // Thinking on: this is a consequential "does this child need outside help?"
  // call. Ceiling raised to 2800 to fit private thinking + the ~650-token answer.
  return askStream(s, userMsg, onChunk, 2800, ADAPTIVE_THINKING, 'referral');
}

// MTSS tier: short, structured. Anchored to the Vermont VTmtss Field Guide (2019).
// The tier is DERIVED by the model from what the teacher described — there is no
// hardcoded/biasing default. The framing is deliberate: this is preparation for the
// school's team conversation, not a determination about the child. The tier decision
// belongs to the school's VTmtss / student support team, never to this tool.
export async function getMTSSAssessment(input: string, initials: string, grade: string, context?: string): Promise<any> {
  const s = `You are a warm brain health educator helping a K-12 teacher get ready to bring a student to their school's team. Never diagnose. Plain language only.

Ground your thinking in the Vermont Multi-tiered System of Supports (VTmtss) Field Guide (2019). In that framework a "tier" describes the LEVEL OF SUPPORT that is delivered — Tier 1 is universal support for all students, Tier 2 is targeted, Tier 3 is intensive and individualized. A tier is not a label on a child and it is not a diagnosis. Your job is to help the teacher see how the behaviors they described line up against what a VTmtss team would consider, and what to bring to the team meeting. It is NOT to make the decision: the decision about what tier of support a student receives belongs to the school's VTmtss / student support team, never to this tool.

Return ONLY valid JSON — no markdown, no backticks:
{"tier":<1, 2, or 3 — the level of support these observations would most likely be discussed under, derived from what the teacher actually described; do not default to any value>,"indicators":["specific observable thing the teacher described","..."],"explanation":"plain-language note on how these observations line up with what a VTmtss team would weigh — framed as preparation for the team conversation, not a determination about the child","supports":["support the teacher can try or suggest 1","support 2","support 3"],"citation":"Framed using the Vermont Multi-tiered System of Supports (VTmtss) Field Guide (2019) and 16 V.S.A. § 2902. The tier of support a student receives is decided by the school's team, not by this tool."}

Never assert a final determination about the student. This is preparation for a team conversation, not a decision.`;
  const userMsg = `${subjectLine(initials, grade)}. Situation: ${input}${contextBlock(context)}`;
  // Thinking on: the tier drives whether a referral is recommended, so the
  // judgment matters. Ceiling raised to 2500 to fit private thinking + the
  // ~300-token JSON answer (truncation here would break the JSON parse).
  const result = await callAnthropic({ model: MODEL, max_tokens: 2500, system: withGuards(s), guards: guardString(), promptKey: 'mtss', role: ACCOUNT_ROLE, messages: [{ role: 'user', content: userMsg }], thinking: ADAPTIVE_THINKING });
  const parsed = safeParseJSON(result);
  // Teacher returns a tier; the parent variant ("What to ask the school for")
  // returns askSchool. Accept either shape.
  if (parsed && (parsed.tier || parsed.askSchool)) return parsed;
  throw new Error('AI_ERROR: Invalid MTSS response');
}

export async function getUrgencyStrategy(input: string): Promise<string> {
  const s = `You are a calm, wise brain health expert. A K-12 teacher needs help RIGHT NOW with a student situation. Give ONE specific, immediately actionable strategy they can use in the next 5 minutes. Write in 2-3 sentences maximum. Be direct, warm, and specific. No jargon. Start with "Right now," or "In this moment,"`;
  try {
    return await ask(s, input, 250, MODEL, 'urgency'); // 2-3 sentences
  } catch (e) {
    console.error('Decode My Student — getUrgencyStrategy failed, using fallback:', e);
    return "Right now, lower your voice to just above a whisper and move slowly toward the student. Say calmly and quietly: 'I can see this is really hard. I'm not going anywhere. When you're ready, I'm here.' Then wait without demanding anything from them.";
  }
}

export async function getCommunicationHelp(situation: string, type: string): Promise<any> {
  const s = `You are an expert communication coach helping a K-12 teacher prepare for a difficult conversation with: ${type}.

Return ONLY valid JSON with these exact keys:
{
  "whatToSay": "Specific suggested language the teacher can actually use. Include exact sentences for opening, middle, and closing of the conversation. Warm and professional.",
  "stayRegulated": "Specific strategies for before, during, and after the conversation to help the teacher stay calm and grounded.",
  "ifItsHard": "Specific language for if the other person gets defensive, angry, or difficult. Include when and how to involve administration if needed.",
  "followUp": "What to document after the conversation, what next steps to communicate, and recommended timeline."
}`;

  try {
    const result = await ask(s, situation, 700, MODEL, 'communication'); // 4-field JSON script
    const parsed = safeParseJSON(result);
    if (parsed && parsed.whatToSay) return parsed;
    throw new Error('Invalid structure');
  } catch (e) {
    console.error('Decode My Student — getCommunicationHelp failed, using fallback:', e);
    return {
      whatToSay: `Open with: "Thank you for taking the time to connect. I'm reaching out because I care about your child and want us to work together." Share specific observations without blame or labels. Close with: "I'd love to hear your perspective. What are you seeing at home? How can we support each other?"`,
      stayRegulated: "Before: Take three slow deep breaths. Remind yourself you both want what is best for the student. During: Speak slowly and pause before responding. If you feel triggered, say 'That is a really important point — let me think about that.' After: Take a short walk before your next task. Write down what was said while it is fresh.",
      ifItsHard: "If they get defensive: 'I hear you. I want to make sure we are on the same team here.' If they get angry: 'I can see this is really upsetting. Let's take a breath and figure this out together.' If it escalates: 'I think it would be helpful to bring in our counselor for our next conversation so we have more support.'",
      followUp: "Document the conversation within 24 hours — include date, who was present, key points discussed, and any agreed next steps. Send a brief follow-up email to confirm what was decided. If a follow-up meeting was agreed on, send the invite within 48 hours."
    };
  }
}

export async function getLibraryContent(topic: string): Promise<string> {
  const s = `You are a warm, knowledgeable brain health educator explaining "${topic}" to K-12 teachers in plain, conversational, non-clinical language.

Cover these areas naturally in flowing paragraphs (NOT bullet points):
1. What this is in plain language
2. What it looks like in the classroom day to day
3. What the student is experiencing internally — what their brain and body are doing
4. What typically triggers it or makes it worse (frame gently — not as teacher mistakes but as common misunderstandings)
5. What genuinely helps — specific, practical, immediately usable

Write warmly, like a knowledgeable colleague talking over coffee. About 400 words. No bullet points. No clinical jargon without immediate plain-language explanation.`;

  try {
    return await ask(s, `Please explain ${topic} for a K-12 teacher.`, 900, MODEL, 'library'); // ~400 words
  } catch (e) {
    console.error('Decode My Student — getLibraryContent failed, using fallback:', e);
    return `${topic} is something many students experience, and understanding it can completely change how you respond — and how effective you are.\n\nIn the classroom, this often shows up in ways that can feel confusing or even frustrating. The behavior you are seeing is not the student being difficult on purpose. Their brain and nervous system are responding to something real, even if you cannot see what that something is.\n\nWhat the student is experiencing internally is often very different from what you see on the outside. They may look defiant when they are actually terrified. They may look checked out when they are actually overwhelmed. The gap between what we see and what is actually happening is where understanding begins.\n\nThe most helpful thing you can do is stay regulated yourself. Your calm nervous system communicates safety to their nervous system. Connection before correction — always. And remember: behavior is communication. When you get curious about what the student is trying to tell you with their behavior, everything shifts.\n\nIf you are seeing this pattern consistently, consider talking with your school counselor about additional support. You do not have to figure this out alone.`;
  }
}

export async function getNextStrategy(originalStrategy: string, outcome: string, studentContext: string): Promise<string> {
  const s = `You are a brain health expert helping a K-12 teacher whose strategy did not fully work with a student. 
The teacher tried: "${originalStrategy}"
The outcome was: ${outcome}
Student context: ${studentContext}

Suggest ONE specific different strategy to try next. Explain briefly why the first one may not have worked and why this new one might. 
3-4 sentences. Warm, direct, specific. No jargon.`;
  try {
    return await ask(s, 'What should I try next?', 250, MODEL, 'nextStrategy'); // 3-4 sentences
  } catch (e) {
    console.error('Decode My Student — getNextStrategy failed, using fallback:', e);
    return "Try shifting from a strategy focus to a connection focus this week. Spend 2 minutes each morning just checking in with this student with no agenda — just genuine curiosity about their day. Sometimes the relationship has to come before the strategy will land.";
  }
}

export async function getSubBrief(studentContext: string, strategies: string, initials: string, grade: string): Promise<string> {
  const s = `You are helping a K-12 teacher create a brief for a substitute teacher covering their class.
The student is identified only by initials ${initials}, Grade: ${grade}. Never use full names.
Write a warm, practical, plain-language substitute brief covering:
1. What this student needs to feel safe and successful (2-3 sentences)
2. What typically triggers difficulty for this student (2-3 sentences)  
3. Two specific things the sub should do if the student struggles (very specific and actionable)
4. One thing the sub should absolutely avoid
5. One encouraging note about this student's strengths

Keep it under 250 words. Warm, practical, no jargon. A sub should be able to read this in 60 seconds and know exactly what to do.`;
  try {
    return await ask(s, `Student context: ${studentContext}\nStrategies that have worked: ${strategies}`, 600, MODEL, 'subBrief'); // under 250 words
  } catch (e) {
    console.error('Decode My Student — getSubBrief failed, using fallback:', e);
    return `SUBSTITUTE BRIEF — ${initials} (${grade})\n\nThis student does best when they feel seen and have a clear sense of what is expected. A warm greeting by name at the start of class goes a long way.\n\nThis student may struggle during transitions between activities or when expectations feel unclear. If you notice tension building, give them a heads-up before transitions: "In five minutes we are moving to math."\n\nIf they struggle: (1) Lower your voice and move closer calmly — do not call attention to them from across the room. (2) Offer a simple choice: "Would you like to start with question 1 or question 3?"\n\nAvoid: Do not escalate by making demands in front of the class. Private, calm redirection works best.\n\nStrength to know: This student has real strengths that show up when they feel safe. Look for moments to notice them doing something well — it makes a real difference.`;
  }
}
